import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface ModuleInfoButtonProps {
  content: React.ReactNode;
  /** Optional: aria label for the info button */
  ariaLabel?: string;
}

/** Gap between the button and the panel, and the smallest margin to a screen edge. */
const GAP = 8;
const EDGE = 12;
const PANEL_WIDTH = 500;
/** Below this the panel spans the screen instead of sitting beside the button. */
const NARROW_SCREEN = 640;

/**
 * Placement is computed rather than declared.
 *
 * The panel used to be `absolute left-full`, which pins it to the right of the
 * button whatever is over there. On a button near the right edge — the invoice
 * review header, for one — the 500px panel ran off the screen and the text was
 * unreadable. `absolute` also meant any ancestor with `overflow:hidden` clipped
 * it, and the invoice modal has exactly that.
 *
 * `position: fixed` against measured coordinates fixes both: nothing can clip
 * it, and it can flip to whichever side has room. It is also rendered through
 * a portal to `document.body` — `fixed` alone escapes clipping but not a
 * `sticky`/z-indexed ancestor's stacking context, which could still paint
 * something else over it regardless of this panel's own z-index.
 */
export const ModuleInfoButton: React.FC<ModuleInfoButtonProps> = ({ content, ariaLabel = 'Module information' }) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // The panel portals to document.body (see the return below), so it is no
  // longer a DOM descendant of containerRef — click-outside has to check both,
  // or a click inside the panel itself would read as "outside" and close it.
  const panelRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Phone: a side panel has nowhere to go, so span the width and drop below.
    if (vw < NARROW_SCREEN) {
      const top = Math.min(r.bottom + GAP, vh - EDGE - 120);
      setPos({
        top: Math.max(EDGE, top),
        left: EDGE,
        width: vw - EDGE * 2,
        maxHeight: vh - top - EDGE,
      });
      return;
    }

    const width = Math.min(PANEL_WIDTH, vw - EDGE * 2);
    const roomRight = vw - r.right - GAP - EDGE;
    const roomLeft = r.left - GAP - EDGE;

    // Prefer the right, flip left when it does not fit, and if neither side
    // has room take the roomier one and clamp to the edge.
    let left: number;
    if (roomRight >= width) left = r.right + GAP;
    else if (roomLeft >= width) left = r.left - GAP - width;
    else left = roomRight >= roomLeft ? Math.max(EDGE, vw - EDGE - width) : EDGE;

    // Top-align with the button, then pull up so the panel cannot hang off the
    // bottom. Never above the top edge.
    const maxHeight = Math.min(vh - EDGE * 2, 692);
    const top = Math.max(EDGE, Math.min(r.top, vh - EDGE - Math.min(maxHeight, 220)));

    setPos({ top, left, width, maxHeight: Math.min(maxHeight, vh - top - EDGE) });
  }, []);

  useEffect(() => {
    if (!visible) return;
    place();

    const onClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      const inContainer = containerRef.current?.contains(target);
      const inPanel = panelRef.current?.contains(target);
      if (!inContainer && !inPanel) setVisible(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setVisible(false);
    };
    // Re-place rather than re-render stale: a fixed panel does not travel with
    // a scrolling ancestor, so it has to be told. Capture catches scrolls in
    // any container, not just the window.
    const onReflow = () => place();

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', onClickOutside);
      document.addEventListener('touchstart', onClickOutside, { passive: true });
    }, 0);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('touchstart', onClickOutside);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [visible, place]);

  return (
    <div ref={containerRef} className="relative inline-flex items-center">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="flex items-center justify-center w-6 h-6 min-w-6 min-h-6 rounded-full border border-slate-400 text-slate-600 hover:bg-slate-100 hover:border-slate-500 transition-colors touch-manipulation shrink-0"
        aria-label={ariaLabel}
        title="Info"
        aria-expanded={visible}
      >
        <span className="text-xs font-semibold leading-none">i</span>
      </button>
      {visible && pos && createPortal(
        // Portalled to document.body rather than rendered as a sibling here:
        // `position: fixed` escapes CLIPPING from any ancestor, but not
        // STACKING — a `sticky`/z-indexed ancestor (Master Catalog's header
        // row, for one) still creates its own stacking context, and a later
        // sibling stacking context at the same z-index wins the paint order
        // regardless of this panel's own z-index, cutting the panel in half
        // behind the table's sticky header. A body-level portal has no such
        // ancestor to be trapped inside.
        <div
          ref={panelRef}
          className="fixed p-4 bg-white border border-[#D7DBD9] rounded-lg shadow-xl z-[100] overflow-hidden flex flex-col"
          style={{
            top: pos.top,
            left: pos.left,
            width: pos.width,
            maxHeight: pos.maxHeight,
            boxShadow: '4px 8px 64px 2px rgba(0, 0, 0, 0.08)',
          }}
          role="dialog"
          aria-label={ariaLabel}
        >
          {/* break-words so a long unbroken token cannot widen the panel and
              push its own text out of view. */}
          <div className="font-sans text-sm leading-[22px] text-slate-900 overflow-y-auto flex-1 min-h-0 pr-1 break-words">
            {content}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
